import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, User, MapPin, Phone, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { requestAccess } from '@/lib/api';

const RequestAccessPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const normalizePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `+91${digits}`;
    if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
    return phone.startsWith('+') ? phone : `+${phone}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.city) {
      toast.error('Please fill all fields');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions to continue');
      return;
    }

    if (formData.phone.replace(/\D/g, '').length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      const res = await requestAccess({
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
      });

      const dealer = res.data;
      if (dealer.status === 'pending') {
        // Could be newly created or already pending
        setSubmitted(true);
        toast.success('Request submitted successfully!');
      } else if (dealer.status === 'approved') {
        toast.error('You are already approved. Please go to login.');
      } else if (dealer.status === 'blocked') {
        toast.error('Your account has been blocked. Please contact support.');
      } else {
        setSubmitted(true);
        toast.success('Request submitted successfully!');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="space-y-3 text-center pb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Request Submitted!</CardTitle>
            <CardDescription className="text-base">Your application is under review</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-slate-600">
              Thank you for your interest in becoming a channel partner with EliteRentals.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <p className="text-sm text-blue-800 font-medium mb-2">Next Steps:</p>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Admin will review your application</li>
                <li>You'll receive approval within 24-48 hours</li>
                <li>Once approved, you can login and start earning</li>
              </ol>
            </div>
            <p className="text-sm text-slate-500">
              We will verify your details and activate your account shortly.
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full"
              data-testid="back-to-login-button"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-3 text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Join EliteRentals</CardTitle>
          <CardDescription className="text-base">
            Request access to become a channel partner
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">Phone Number *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="phone"
                  data-testid="phone-input"
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  className="pl-10 h-11"
                  maxLength={10}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium">City *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="city"
                  data-testid="city-input"
                  type="text"
                  placeholder="Your city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  data-testid="terms-checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  required
                />
                <label htmlFor="terms" className="text-sm text-slate-600">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-slate-900 font-medium hover:underline"
                  >
                    Terms & Conditions
                  </button>{' '}
                  <span className="text-red-500">*</span>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-request-button"
              className="w-full h-12 text-base font-medium mt-6"
              disabled={loading || !termsAccepted}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Request Access'
              )}
            </Button>

            <div className="text-center pt-4 border-t">
              <p className="text-sm text-slate-600">
                Already approved?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-slate-900 font-medium hover:underline"
                >
                  Login here
                </button>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Terms & Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <CardTitle>Channel Partner Terms & Conditions</CardTitle>
              <CardDescription>Last Updated: January 2025</CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh] py-6 space-y-4 text-sm">
              <p className="text-slate-700">
                These Terms & Conditions govern participation in the EliteRentals Channel Partner Program.
              </p>
              <p className="text-slate-700">
                By registering on the Channel Partner Portal, the partner agrees to the following:
              </p>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">1. Eligibility</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>The program is open to real estate agents, brokers, and property consultants.</li>
                    <li>EliteRentals reserves the right to approve, suspend, or terminate any partner account at its discretion.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">2. Referral Eligibility Rules</h3>
                  <p className="text-slate-700 mb-2">A referral will be considered valid only if all conditions below are met:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>The customer is new to EliteRentals (no prior completed orders).</li>
                    <li>The referral applies only to the customer's first successful order.</li>
                    <li>The rental order must be for a minimum duration of 6 months.</li>
                    <li>Referral must originate via the official referral link or QR code.</li>
                    <li>Only one channel partner can be associated with a customer.</li>
                    <li>Referral is valid only within the referral validity window defined by EliteRentals.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">3. Commission Structure</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>Channel Partner earns 50% of the first-month brokerage/rent.</li>
                    <li>Commission status will be marked as Pending initially.</li>
                    <li>Commission becomes Confirmed only after:
                      <ul className="list-disc list-inside ml-6 mt-1">
                        <li>Successful delivery of items, and</li>
                        <li>First month's rent/payment is received by EliteRentals.</li>
                      </ul>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">4. Payout & Cooling Period</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>Commission payouts are subject to a cooling period (typically 15–30 days).</li>
                    <li>If an order is cancelled, refunded, or disputed within this period:
                      <ul className="list-disc list-inside ml-6 mt-1">
                        <li>Commission may be revoked.</li>
                      </ul>
                    </li>
                    <li>EliteRentals reserves the right to withhold payouts for verification or review.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">5. Prohibited Activities</h3>
                  <p className="text-slate-700 mb-2">The following will lead to immediate disqualification:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>Self-referrals (partner referring themselves or related parties)</li>
                    <li>Manipulating referral links or systems</li>
                    <li>Creating fake customers or orders</li>
                    <li>Misrepresentation of EliteRentals services</li>
                    <li>Any fraudulent or unethical activity</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">6. Termination</h3>
                  <p className="text-slate-700 mb-2">EliteRentals may suspend or terminate a partner account:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>Without prior notice in case of fraud or misuse</li>
                    <li>With immediate effect if terms are violated</li>
                    <li>Pending commissions in such cases may be forfeited.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">7. Final Authority</h3>
                  <ul className="list-disc list-inside space-y-1 text-slate-700">
                    <li>All decisions regarding referral validity, commissions, and payouts are final and binding.</li>
                    <li>EliteRentals reserves the right to update these terms at any time.</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-800 font-medium">
                    📌 By continuing to use the Channel Partner Portal, you acknowledge and accept these Terms & Conditions.
                  </p>
                </div>
              </div>
            </CardContent>
            <div className="border-t p-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowTerms(false)}
                data-testid="close-terms-button"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTerms(false);
                }}
                data-testid="accept-terms-button"
              >
                Accept & Continue
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RequestAccessPage;
